import React from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { Breadcrumbs, breadcrumbJsonLd } from "../components/site/Breadcrumbs";
import { usePageMeta, SITE_PUBLIC_URL } from "../components/site/usePageMeta";
import { HUBS, LOWCOUNTRY_CITIES, CONNECTION_STEPS } from "../content/hubs";
import NotFound from "./NotFound";

export default function ServiceHub() {
  const { group } = useParams();
  const hub = HUBS[group];

  const path = hub ? `/sc/${group}/` : null;
  const crumbs = [{ label: "Home", href: "/" }, { label: hub?.name || "" }];
  usePageMeta({
    title: hub?.metaTitle || "Home Pro Connect",
    description: hub?.metaDescription || "",
    path,
    jsonLd: hub ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: `${hub.name} connections`,
          description: hub.metaDescription,
          url: `${SITE_PUBLIC_URL}${path}`,
          provider: { "@type": "Organization", name: "Home Pro Connect", url: SITE_PUBLIC_URL },
          areaServed: LOWCOUNTRY_CITIES.map((c) => ({ "@type": "City", name: `${c}, SC` })),
        },
        breadcrumbJsonLd(crumbs),
      ],
    } : null,
  });

  if (!hub) return <NotFound />;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main data-testid="hub-page" data-group={group}>
        <section className="bg-[#133955] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-14 md:pt-32 md:pb-16">
            <Breadcrumbs items={crumbs} />
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{hub.h1}</h1>
            {hub.intro.map((p, i) => (
              <p key={i} className="mt-4 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">{p}</p>
            ))}
            <div className="mt-8">
              <a href="/#request" data-testid="hub-cta-top" className="group inline-flex items-center gap-2 rounded-full bg-[#54BCE6] text-[#133955] px-8 py-4 font-bold hover:bg-white transition-colors duration-300">
                Request a Connection
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight max-w-2xl">{hub.approachTitle}</h2>
            <div className="mt-6 space-y-5 max-w-3xl">
              {hub.approach.map((p, i) => (
                <p key={i} className="text-[#173042]/85 text-[15px] md:text-base leading-relaxed">{p}</p>
              ))}
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {hub.items.map((item) => (
                <a
                  key={item.slug}
                  href={`/sc/${group}/${item.slug}/`}
                  data-testid={`hub-card-${item.slug}`}
                  className="group rounded-2xl border border-[rgba(19,57,85,0.08)] bg-white p-6 hover:border-[#54BCE6] hover:shadow-[0_16px_40px_rgba(19,57,85,0.10)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-xl font-bold text-[#133955] group-hover:text-[#54BCE6] transition-colors">{item.name}</h3>
                    <span className="grid place-items-center w-8 h-8 rounded-full bg-[#EAF7FC] text-[#133955] shrink-0 group-hover:bg-[#54BCE6] transition-colors">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                  <p className="mt-2.5 text-sm text-[#173042]/70 leading-relaxed">{item.metaDescription.split(".")[0]}.</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-[#EAF7FC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">How the connection works</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {CONNECTION_STEPS.map((s) => (
                <div key={s.num} className="rounded-2xl bg-white p-7 border border-[rgba(19,57,85,0.08)]">
                  <span className="font-display text-4xl font-extrabold text-[#54BCE6]">{s.num}</span>
                  <h3 className="mt-3 font-display text-xl font-bold text-[#133955]">{s.title}</h3>
                  <p className="mt-2 text-[#173042]/75 text-[15px] leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-5 py-4 max-w-3xl">
              <CheckCircle2 size={18} className="text-[#54BCE6] shrink-0 mt-0.5" />
              <p className="text-sm text-[#173042]/75">
                Home Pro Connect is a connection and referral service. Contractors are independent third parties, and all
                estimates, agreements, and work are between you and the contractor you choose.
              </p>
            </div>
            <div className="mt-10 text-center">
              <a href="/#request" data-testid="hub-cta-bottom" className="group inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300">
                Request a Connection
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
